import type { RefObject } from 'react'

export const quoteRe = /^(\s*)>(\s*)/

export const getIndentation = (line: string) => {
  const m = line.match(/^(\s*)/)
  return m ? m[1] : ''
}

export const getIndentationAndContent = (line: string) => {
  const indentation = getIndentation(line)
  return [indentation, line.slice(indentation.length)]
}

export const getLineBounds = (text: string, pos: number) => {
  const lineStart = getLineStart(text, pos)
  const lineEnd = getLineEnd(text, pos)
  const line = text.slice(lineStart, lineEnd)
  return { lineStart, lineEnd, line }
}

export const getLineStart = (text: string, pos: number): number =>
  text.lastIndexOf('\n', pos - 1) + 1

export const getLineEnd = (text: string, pos: number): number => {
  const idx = text.indexOf('\n', pos)
  return idx === -1 ? text.length : idx
}

export const getPrecedingLine = (text: string, pos: number) => {
  let endIndex = pos - 1
  if (endIndex >= 0 && text.charCodeAt(endIndex) === 10 /* '\n' */) {
    endIndex -= 1
  }

  if (endIndex < 0) {
    return { line: '', lineStart: 0 }
  }

  const lineStart = text.lastIndexOf('\n', endIndex) + 1
  return { line: text.slice(lineStart, endIndex + 1), lineStart }
}

export type SelectedLinesInfo = {
  before: string
  lines: string[]
  after: string
  startLineStart: number
  endLineStart: number
  endLineLength: number
}

export const getSelectedLinesInfo = (
  text: string,
  s: number,
  e: number,
): SelectedLinesInfo => {
  const startLineStart = getLineStart(text, s)
  const endIndex = e > s ? e - 1 : s
  const endInfo = getLineBounds(text, endIndex)

  const before = text.slice(0, startLineStart)
  const middle = text.slice(
    startLineStart,
    endInfo.lineStart + endInfo.line.length,
  )
  const after = text.slice(endInfo.lineStart + endInfo.line.length)

  const lines = middle.split('\n')

  return {
    before,
    lines,
    after,
    startLineStart,
    endLineStart: endInfo.lineStart,
    endLineLength: endInfo.line.length,
  }
}

type NodeGetter = () => HTMLTextAreaElement | null

const rafFocusAndSelection = (
  getNode: NodeGetter,
  nextSelStart: number,
  nextSelEnd: number,
  after?: () => void,
) => {
  requestAnimationFrame(() => {
    const node = getNode()
    if (!node) return
    if (document.activeElement !== node) {
      node.focus({ preventScroll: true })
    }
    node.setSelectionRange(nextSelStart, nextSelEnd)
    if (after) requestAnimationFrame(after)
  })
}

export const applyChange = (
  ref: RefObject<HTMLTextAreaElement>,
  onChange: (v: string) => void,
  nextValue: string,
  nextSelStart: number,
  nextSelEnd: number = nextSelStart,
) => {
  onChange(nextValue)
  rafFocusAndSelection(() => ref.current, nextSelStart, nextSelEnd)
}

export const insertAtSelection = (
  before: string,
  insert: string,
  after: string,
): { next: string; caret: number } => {
  const next = before + insert + after
  const caret = (before + insert).length
  return { next, caret }
}

export const addLineIndent = (indentStep: number) => (ln: string) => {
  if (indentStep === 0) return ln

  if (indentStep > 0) {
    return ' '.repeat(indentStep) + ln
  }

  const indentStr = getIndentation(ln)
  if (!indentStr) return ln

  const remove = Math.min(indentStr.length, Math.abs(indentStep))
  const newIndentLen = Math.max(0, indentStr.length - remove)
  return ' '.repeat(newIndentLen) + ln.slice(indentStr.length)
}

type LineTransformer = (
  line: string,
  meta: {
    index: number
    curStart: number
    isStartLine: boolean
  },
) => string

export const transformSelectedLines = (
  text: string,
  s: number,
  e: number,
  mapLine: LineTransformer,
  pre?: SelectedLinesInfo,
): {
  newText: string
  selectionStart: number
  selectionEnd: number
  changed: boolean
} => {
  const info = pre ?? getSelectedLinesInfo(text, s, e)

  let deltaStart = 0
  let deltaEnd = 0
  let accIndex = info.startLineStart

  const transformed = info.lines.map((ln, index) => {
    const curStart = accIndex
    const isStartLine = s >= curStart && s <= curStart + ln.length

    const outLn = mapLine(ln, { index, curStart, isStartLine })
    const change = outLn.length - ln.length

    if (isStartLine) deltaStart += change
    // Any change occurring before or at the original selection end should shift the end
    if (e >= curStart) deltaEnd += change

    accIndex += ln.length + 1
    return outLn
  })

  const newText = info.before + transformed.join('\n') + info.after
  const changed = newText !== text

  const selectionStart = s + deltaStart
  const selectionEnd = s === e ? selectionStart : e + deltaEnd
  return { newText, selectionStart, selectionEnd, changed }
}

export const buildMultilineAction =
  <CTX, O = undefined>(
    computeContext: (info: SelectedLinesInfo, options?: O) => CTX | null,
    buildLineTransformer: (ctx: CTX) => LineTransformer,
  ) =>
  (
    textareaRef: RefObject<HTMLTextAreaElement>,
    value: string,
    onChange: (val: string) => void,
    options?: O,
  ): boolean => {
    const el = textareaRef.current
    if (!el) return false
    const s = el.selectionStart ?? 0
    const e = el.selectionEnd ?? s

    const info = getSelectedLinesInfo(value, s, e)
    const ctx = computeContext(info, options)

    if (!ctx) return false
    const transformLine = buildLineTransformer(ctx)

    const { newText, selectionStart, selectionEnd, changed } =
      transformSelectedLines(value, s, e, transformLine)

    if (!changed) return false

    applyChange(textareaRef, onChange, newText, selectionStart, selectionEnd)
    return true
  }
